// For Android: Render Backend
// For Web: Render Backend (Hybrid Deployment)
const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : 'https://rajieev-house-rental.onrender.com/api';

// JWT Token helper — reads from localStorage
const getToken = () => localStorage.getItem('auth_token') || null;
const getAuthHeaders = (extra = {}) => {
    const token = getToken();
    const headers = { 'Content-Type': 'application/json', ...extra };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
};

// Force logout helper — clears all auth state (NO reload to avoid loops)
const forceLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('loggedIn');
    localStorage.removeItem('role');
    localStorage.removeItem('currUserId');
};

// Safe fetch wrapper with retry logic for Render cold starts + auto auth headers
const safeFetch = async (url, options = {}, retries = 2) => {
    // Auto-inject auth headers if not already present
    if (!options.headers || !options.headers['Authorization']) {
        options.headers = { ...options.headers, 'Authorization': `Bearer ${getToken()}` };
    }
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const res = await fetch(url, options);
            // If 401, token expired — clear auth state silently
            if (res.status === 401) {
                forceLogout();
                return null;
            }
            return res;
        } catch (e) {
            console.error(`API call failed (attempt ${attempt + 1}): ${url}`, e);
            if (attempt < retries) {
                await new Promise(r => setTimeout(r, 1000 * (attempt + 1))); // Exponential backoff
            }
        }
    }
    return null;
};

// FIX: Safe JSON parse from response
const safeJson = async (res, fallback = null) => {
    if (!res) return fallback;
    try {
        return await res.json();
    } catch (e) {
        console.error("Failed to parse JSON response", e);
        return fallback;
    }
};

export const api = {
    // Auth — login does NOT need auth token (public endpoint)
    login: async (role, ph, password) => {
        try {
            const res = await fetch(`${API_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role, ph, password })
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    selectOrg: async (tenantId, ph, password) => {
        try {
            const res = await fetch(`${API_URL}/login/select-org`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tenantId, ph, password })
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Get all orgs for current tenant
    getMyOrgs: async () => {
        const res = await safeFetch(`${API_URL}/my-orgs`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, orgs: [] });
    },

    // Switch org without re-login (authenticated)
    switchOrg: async (targetOrgId) => {
        try {
            const res = await fetch(`${API_URL}/switch-org`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ targetOrgId })
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
                if (data.user?.id) localStorage.setItem('currUserId', data.user.id);
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // ── OTP-based password reset (replaces the legacy single /forgot-password) ──
    // Step 1: ask the server to send a 6-digit OTP via WhatsApp.
    // Always responds 200 to prevent phone enumeration — UI should show
    // "If your number is registered, you'll get a WhatsApp OTP shortly."
    requestPasswordResetOtp: async (role, phone) => {
        try {
            const res = await fetch(`${API_URL}/forgot-password/request-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role, phone })
            });
            return await res.json();
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Step 2: submit OTP + new password.
    verifyPasswordResetOtp: async (role, phone, otp, newPassword) => {
        try {
            const res = await fetch(`${API_URL}/forgot-password/verify-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role, phone, otp, newPassword })
            });
            return await res.json();
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Legacy single-call reset — kept as a thin shim that returns the 410
    // server response so any old caller surfaces a clear error.
    forgotPassword: async () => {
        return { success: false, message: 'Reset method updated. Please use the new OTP flow.' };
    },
    // Change password for the currently logged-in user. Requires JWT + current password.
    // Intentionally does NOT use safeFetch because a 401 here means "current password wrong",
    // not "token expired" — we don't want to force-logout on that.
    changePassword: async (currentPassword, newPassword) => {
        try {
            const res = await fetch(`${API_URL}/change-password`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ currentPassword, newPassword })
            });
            return await res.json();
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },
    signup: async (role, ph, password, name, orgName, skill, termsVersion) => {
        try {
            const res = await fetch(`${API_URL}/signup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ role, ph, password, name, orgName, skill, termsVersion })
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Google Sign-In — verify credential with backend, get JWT
    googleLogin: async (credential, signupRole, phone, orgName, skill, displayName, termsVersion) => {
        try {
            const body = { credential };
            if (signupRole) body.signupRole = signupRole;
            if (phone) body.phone = phone;
            if (orgName) body.orgName = orgName;
            if (skill) body.skill = skill;
            if (displayName) body.displayName = displayName;
            if (termsVersion) body.termsVersion = termsVersion;
            const res = await fetch(`${API_URL}/auth/google`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
            const data = await res.json();
            if (data.success && data.token) {
                localStorage.setItem('auth_token', data.token);
                if (data.orgId) localStorage.setItem('orgId', data.orgId);
            }
            return data;
        } catch (e) {
            return { success: false, message: "Network Error: " + e.message };
        }
    },

    // Logout — clear token
    logout: () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('role');
        localStorage.removeItem('currUserId');
        localStorage.removeItem('googleUser');
    },

    // Sync Data
    sync: async () => {
        try {
            const res = await fetch(`${API_URL}/sync`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (!res.ok) {
                if (res.status === 401) {
                    forceLogout();
                    return null;
                }
                console.error("Sync HTTP error:", res.status);
                return null;
            }
            let data = await res.json();

            // Recover cached maintenance data from LocalStorage as backup
            const cachedMts = JSON.parse(localStorage.getItem('maint_assignees') || '{}');
            if (data && data.maints) {
                data.maints = data.maints.map(m => {
                    if (cachedMts[m.id]) {
                        // Only use cache if server values are empty
                        return {
                            ...m,
                            asn_n: m.asn_n || cachedMts[m.id].asn_n,
                            asn_p: m.asn_p || cachedMts[m.id].asn_p,
                            asn_notes: m.asn_notes || cachedMts[m.id].asn_notes,
                            exp_amt: m.exp_amt || cachedMts[m.id].exp_amt || 0,
                            exp_target: m.exp_target || cachedMts[m.id].exp_target || 'all',
                            exp_applied: m.exp_applied || cachedMts[m.id].exp_applied || false
                        };
                    }
                    return m;
                });
            }
            return data;
        } catch (e) {
            console.error("Sync failed:", e);
            return null;
        }
    },

    // Properties
    saveProperty: async (prop) => {
        const res = await safeFetch(`${API_URL}/properties`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(prop)
        });
        return safeJson(res, { success: false });
    },

    deleteProperty: async (id) => {
        const res = await safeFetch(`${API_URL}/properties/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Tenants
    updateProfile: async (data) => {
        const res = await safeFetch(`${API_URL}/profile`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    saveTenant: async (ten) => {
        const res = await safeFetch(`${API_URL}/tenants`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(ten)
        });
        return safeJson(res, { success: false });
    },

    // Tenant Association Requests (consent-based linking)
    lookupTenant: async (phone) => {
        const res = await safeFetch(`${API_URL}/tenants/lookup/${encodeURIComponent(phone)}`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    },
    sendTenantRequest: async (data) => {
        const res = await safeFetch(`${API_URL}/tenants/request`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return safeJson(res, { success: false });
    },
    getMyRequests: async () => {
        const res = await safeFetch(`${API_URL}/tenants/my-requests`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, requests: [] });
    },
    respondToRequest: async (reqId, action) => {
        const res = await safeFetch(`${API_URL}/tenants/request/${reqId}/respond`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ action }) });
        return safeJson(res, { success: false });
    },
    getLandlordRequests: async () => {
        const res = await safeFetch(`${API_URL}/tenants/requests`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, requests: [] });
    },
    convertLead: async (leadId) => {
        const res = await safeFetch(`${API_URL}/tenants/from-lead`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ leadId }) });
        return safeJson(res, { success: false });
    },

    deleteTenant: async (id) => {
        const res = await safeFetch(`${API_URL}/tenants/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Payments
    savePayment: async (pay) => {
        const res = await safeFetch(`${API_URL}/payments`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(pay)
        });
        return safeJson(res, { success: false });
    },

    deletePayment: async (id) => {
        const res = await safeFetch(`${API_URL}/payments/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Messages
    sendMessage: async (msg) => {
        const res = await safeFetch(`${API_URL}/messages`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(msg)
        });
        return safeJson(res, { success: false });
    },

    // Notifications
    saveNotification: async (notif) => {
        try {
            await fetch(`${API_URL}/notifications`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(notif)
            });
        } catch (e) { console.error("Notif save failed:", e); }
    },

    markRead: async (role, tid) => {
        const res = await safeFetch(`${API_URL}/notifications/read`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ role, tid })
        });
        return safeJson(res, { success: false });
    },

    // --- Web Push ---
    // Public — VAPID public key (no auth needed). Cached at the call site.
    getVapidKey: async () => {
        try {
            const res = await fetch(`${API_URL}/push/vapid-key`);
            return await res.json();
        } catch (e) {
            console.error("VAPID key fetch failed:", e);
            return { key: '' };
        }
    },
    // Register or refresh this device's push subscription. `sub` is the
    // shape you get from PushSubscription.toJSON() in the browser.
    subscribePush: async (sub) => {
        const res = await safeFetch(`${API_URL}/push/subscribe`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(sub),
        });
        return safeJson(res, { success: false });
    },
    // Remove a device subscription by endpoint URL.
    unsubscribePush: async (endpoint) => {
        const res = await safeFetch(`${API_URL}/push/unsubscribe`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ endpoint }),
        });
        return safeJson(res, { success: false });
    },

    // Maintenance
    saveMaintenance: async (maint) => {
        // Cache maintenance data locally as backup
        if (maint.asn_n || maint.asn_p || maint.asn_notes || maint.exp_amt) {
            try {
                const cache = JSON.parse(localStorage.getItem('maint_assignees') || '{}');
                cache[maint.id] = { ...cache[maint.id], asn_n: maint.asn_n, asn_p: maint.asn_p, asn_notes: maint.asn_notes, exp_amt: maint.exp_amt || 0, exp_target: maint.exp_target || 'all', exp_applied: maint.exp_applied || false };
                localStorage.setItem('maint_assignees', JSON.stringify(cache));
            } catch (e) { console.error("Cache write failed:", e); }
        }

        const res = await safeFetch(`${API_URL}/maintenance`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(maint)
        });
        return safeJson(res, { success: false });
    },

    deleteMaintenance: async (id) => {
        // Also clean up local cache
        try {
            const cache = JSON.parse(localStorage.getItem('maint_assignees') || '{}');
            delete cache[id];
            localStorage.setItem('maint_assignees', JSON.stringify(cache));
        } catch (e) { /* ignore */ }

        const res = await safeFetch(`${API_URL}/maintenance/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Common Charges
    saveCC: async (cc) => {
        const res = await safeFetch(`${API_URL}/common_charges`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(cc)
        });
        return safeJson(res, { success: false });
    },

    deleteCC: async (id) => {
        const res = await safeFetch(`${API_URL}/common_charges/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Vacate
    requestVacate: async (tid) => {
        const res = await safeFetch(`${API_URL}/vacate/request`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ tid })
        });
        return safeJson(res, { success: false });
    },

    approveVacate: async (tid, pid) => {
        const res = await safeFetch(`${API_URL}/vacate/approve`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ tid, pid })
        });
        return safeJson(res, { success: false });
    },

    rejectVacate: async (tid) => {
        const res = await safeFetch(`${API_URL}/vacate/reject`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ tid })
        });
        return safeJson(res, { success: false });
    },

    // Settings (with localStorage cache so UPI ID etc. persist when server sleeps)
    getSettings: async () => {
        try {
            const res = await fetch(`${API_URL}/settings`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (!res.ok) throw new Error('Settings fetch failed');
            const data = await res.json();
            // Cache settings locally for offline/sleep fallback
            if (data && Object.keys(data).length > 0) {
                localStorage.setItem('cached_settings', JSON.stringify(data));
            }
            return data;
        } catch (e) {
            console.error("Failed to fetch settings, using cache:", e);
            // Return cached settings if server is unavailable
            try {
                const cached = JSON.parse(localStorage.getItem('cached_settings') || '{}');
                return cached;
            } catch { return {}; }
        }
    },

    saveSetting: async (key, value) => {
        const res = await safeFetch(`${API_URL}/settings`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ key, value })
        });
        return safeJson(res, { success: false });
    },

    // Service Person sync
    serviceSync: async (spId) => {
        try {
            const res = await fetch(`${API_URL}/service/sync/${spId}`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (!res.ok) return null;
            return await res.json();
        } catch (e) { console.error("Service sync failed:", e); return null; }
    },

    serviceUpdateJob: async (data) => {
        const res = await safeFetch(`${API_URL}/service/update-job`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    serviceVerifyCompletion: async (token, tid) => {
        const res = await safeFetch(`${API_URL}/service/verify-completion`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ token, tid })
        });
        return safeJson(res, { success: false });
    },

    saveServicePerson: async (sp) => {
        const res = await safeFetch(`${API_URL}/service_persons`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(sp)
        });
        return safeJson(res, { success: false });
    },

    deleteServicePerson: async (id) => {
        const res = await safeFetch(`${API_URL}/service_persons/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    saveServicePayment: async (pay) => {
        const res = await safeFetch(`${API_URL}/service_payments`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(pay)
        });
        return safeJson(res, { success: false });
    },

    // Leases
    getLeases: async () => {
        const res = await safeFetch(`${API_URL}/leases`, { headers: getAuthHeaders() });
        return safeJson(res, []);
    },
    saveLease: async (lease) => {
        const res = await safeFetch(`${API_URL}/leases`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(lease)
        });
        return safeJson(res, { success: false });
    },

    deleteLease: async (id) => {
        const res = await safeFetch(`${API_URL}/leases/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false });
    },

    // Admin
    adminGetOrgs: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/orgs${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, orgs: [] });
    },

    adminGetOrg: async (id) => {
        const res = await safeFetch(`${API_URL}/admin/orgs/${id}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, org: null });
    },

    adminPatchOrg: async (id, data) => {
        const res = await safeFetch(`${API_URL}/admin/orgs/${id}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    adminGetStats: async () => {
        const res = await safeFetch(`${API_URL}/admin/stats`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, stats: {} });
    },

    adminGetProperties: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/properties${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, properties: [] });
    },

    adminGetTenants: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/tenants${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, tenants: [] });
    },

    adminGetServicePersons: async (params = {}) => {
        const qs = new URLSearchParams(params).toString();
        const res = await safeFetch(`${API_URL}/admin/service-persons${qs ? '?' + qs : ''}`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, servicePersons: [] });
    },

    adminGetPricing: async () => {
        const res = await safeFetch(`${API_URL}/admin/pricing`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, config: {} });
    },

    adminUpdatePricing: async (data) => {
        const res = await safeFetch(`${API_URL}/admin/pricing`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    adminGetAuditLogs: async () => {
        const res = await safeFetch(`${API_URL}/admin/audit-logs`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, logs: [] });
    },

    // Admin Reports
    adminReport: async (type) => {
        const res = await safeFetch(`${API_URL}/admin/reports/${type}`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, data: [] });
    },

    // Plans (Dynamic Pricing Engine)
    getPlans: async () => {
        const res = await safeFetch(`${API_URL}/plans`);
        return safeJson(res, { success: false, plans: [] });
    },

    adminGetPlans: async () => {
        const res = await safeFetch(`${API_URL}/admin/plans`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, plans: [] });
    },

    // Free-trial config (super-admin only): controls whether new landlord
    // signups get N days of a paid plan before being downgraded to Free.
    adminGetTrialConfig: async () => {
        const res = await safeFetch(`${API_URL}/admin/trial-config`, {
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false, config: null });
    },
    adminSetTrialConfig: async (config) => {
        const res = await safeFetch(`${API_URL}/admin/trial-config`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(config),
        });
        return safeJson(res, { success: false });
    },

    adminCreatePlan: async (data) => {
        const res = await safeFetch(`${API_URL}/admin/plans`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    adminUpdatePlan: async (id, data) => {
        const res = await safeFetch(`${API_URL}/admin/plans/${id}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    adminDeletePlan: async (id) => {
        const res = await safeFetch(`${API_URL}/admin/plans/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false });
    },

    // Billing (Phase 14)
    createBillingOrder: async (planId) => {
        const res = await safeFetch(`${API_URL}/billing/create-order`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({ plan_id: planId })
        });
        return safeJson(res, { success: false });
    },

    verifyBillingPayment: async (paymentData) => {
        const res = await safeFetch(`${API_URL}/billing/verify-payment`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(paymentData)
        });
        return safeJson(res, { success: false });
    },

    getBillingHistory: async () => {
        const res = await safeFetch(`${API_URL}/billing/history`, {
            headers: getAuthHeaders()
        });
        return safeJson(res, { success: false, transactions: [] });
    },

    // Razorpay Rent Payment (tenant pays rent online)
    createRentOrder: async (data) => {
        const res = await safeFetch(`${API_URL}/payments/razorpay-order`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    verifyRentPayment: async (data) => {
        const res = await safeFetch(`${API_URL}/payments/razorpay-verify`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(data)
        });
        return safeJson(res, { success: false });
    },

    // Phase 16: Public property browsing (no auth)
    getPublicProperties: async (prefs = {}) => {
        try {
            const params = new URLSearchParams();
            if (prefs.rooms) params.set('rooms', prefs.rooms);
            if (prefs.max_rent) params.set('max_rent', prefs.max_rent);
            if (prefs.furnishing && prefs.furnishing !== 'any') params.set('furnishing', prefs.furnishing);
            if (prefs.property_type && prefs.property_type !== 'any') params.set('property_type', prefs.property_type);
            if (prefs.comm_type && prefs.comm_type !== 'any') params.set('comm_type', prefs.comm_type);
            const qs = params.toString();
            const res = await fetch(`${API_URL}/public/properties${qs ? '?' + qs : ''}`);
            if (!res.ok) return { success: false, properties: [] };
            return await res.json();
        } catch (e) {
            console.error("Public properties fetch failed:", e);
            return { success: false, properties: [] };
        }
    },

    // Phase 22: Get user's favorite property IDs
    getFavorites: async () => {
        const res = await safeFetch(`${API_URL}/user/favorites`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, favorites: [] });
    },

    // Phase 22: Toggle favorite (add/remove)
    toggleFavorite: async (propertyId) => {
        const res = await safeFetch(`${API_URL}/user/favorites/${propertyId}`, {
            method: 'POST', headers: getAuthHeaders()
        });
        return safeJson(res, { success: false });
    },

    // Phase 22: Get saved browse preferences
    getPreferences: async () => {
        const res = await safeFetch(`${API_URL}/user/preferences`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, preferences: {} });
    },

    // Phase 22: Save browse preferences
    savePreferences: async (prefs) => {
        const res = await safeFetch(`${API_URL}/user/preferences`, {
            method: 'PUT', headers: getAuthHeaders(),
            body: JSON.stringify(prefs)
        });
        return safeJson(res, { success: false });
    },

    // Phase 16: Submit lead inquiry (no auth)
    submitLead: async (lead) => {
        try {
            const res = await fetch(`${API_URL}/leads`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(lead)
            });
            return await res.json();
        } catch (e) {
            return { success: false, message: 'Network error: ' + e.message };
        }
    },

    // Phase 16: Get leads (landlord)
    getLeads: async () => {
        const res = await safeFetch(`${API_URL}/leads`, {
            headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        return safeJson(res, { success: false, leads: [] });
    },

    // Phase 16: Update lead status (landlord)
    updateLead: async (id, status) => {
        const res = await safeFetch(`${API_URL}/leads/${id}`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ status })
        });
        return safeJson(res, { success: false });
    },

    // Phase 18: Schedule visit for a lead
    scheduleLead: async (id, visit_date, visit_time) => {
        const res = await safeFetch(`${API_URL}/leads/${id}/schedule`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ visit_date, visit_time })
        });
        return safeJson(res, { success: false });
    },

    // Phase 18: Update visit status (completed/missed)
    updateVisitStatus: async (id, visit_status) => {
        const res = await safeFetch(`${API_URL}/leads/${id}/visit-status`, {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify({ visit_status })
        });
        return safeJson(res, { success: false });
    },

    // Auto-generate due payments
    generateDuePayments: async () => {
        try {
            const res = await fetch(`${API_URL}/payments/generate-due`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (!res.ok) return { success: false, generated: 0 };
            return await res.json();
        } catch (e) {
            console.error("Generate due payments failed:", e);
            return { success: false, generated: 0 };
        }
    },

    // Team management
    getTeam: async () => {
        const res = await safeFetch(`${API_URL}/team`, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, members: [] });
    },
    addTeamMember: async (data) => {
        const res = await safeFetch(`${API_URL}/team`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return safeJson(res, { success: false });
    },
    updateTeamMember: async (id, data) => {
        const res = await safeFetch(`${API_URL}/team/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return safeJson(res, { success: false });
    },
    removeTeamMember: async (id) => {
        const res = await safeFetch(`${API_URL}/team/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    },

    // Feedback / Bug Reports
    submitFeedback: async (data) => {
        const res = await safeFetch(`${API_URL}/feedback`, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(data) });
        return safeJson(res, { success: false });
    },
    getFeedback: async (status) => {
        const url = status ? `${API_URL}/admin/feedback?status=${status}` : `${API_URL}/admin/feedback`;
        const res = await safeFetch(url, { headers: getAuthHeaders() });
        return safeJson(res, { success: false, feedback: [] });
    },
    updateFeedbackStatus: async (id, status) => {
        const res = await safeFetch(`${API_URL}/admin/feedback/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify({ status }) });
        return safeJson(res, { success: false });
    },
    deleteFeedback: async (id) => {
        const res = await safeFetch(`${API_URL}/admin/feedback/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
        return safeJson(res, { success: false });
    }
};
